import React, { useEffect, useMemo, useState } from 'react';
import {
  XMarkIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { Share2 } from 'lucide-react';
import { Factura } from './types';
import { FeEstadoBadge } from './FeEstadoBadge';
import { useAuthStore } from '../../stores/useAuthStore';
import { usePaymentMethodsStore } from '../../stores/usePaymentMethodsStore';
import { estadoPagoFacturaLabel, metodoPagoFacturaLabel } from '../../utils/facturaPdfHtml';
import {
  buildFacturaPdfBlob,
  downloadFacturaPdfBlob,
  openFacturaPdfBlobInNewTab,
  safeFacturaPdfFilename,
  shareFacturaPdfBlob,
  canSharePdfFiles,
} from '../../utils/facturaPdfBlob';
import { numeroFacturaParaMostrar } from '../../utils/facturaNumeroCanal';
import { labelFacturaItemCantidad, labelFacturaItemProducto } from '../../utils/productoEstado';
import { HistorialPagosPanel } from './HistorialPagosPanel';
import { formatearMoneda } from '../../utils/formatters';
import { PaymentMethodMedia } from '../shared/PaymentMethodMedia';

interface VistaFacturaProps {
  factura: Factura;
  onClose: () => void;
  onEnviar: () => void;
}

export const VistaFactura: React.FC<VistaFacturaProps> = ({ factura, onClose, onEnviar }) => {
  const [pdfBusy, setPdfBusy] = useState(false);
  const shareAvailable = useMemo(() => canSharePdfFiles(), []);
  const { currentEmpresa } = useAuthStore();
  const { fetchPaymentMethods } = usePaymentMethodsStore();
  const empresaId = currentEmpresa?.id ?? '';
  const paymentMethods = usePaymentMethodsStore((s) => {
    if (!empresaId) return [];
    return s.paymentMethods
      .filter((pm) => pm.empresa_id === empresaId && pm.is_active && pm.show_on_invoices)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  });

  useEffect(() => {
    if (empresaId) void fetchPaymentMethods(empresaId);
  }, [empresaId, fetchPaymentMethods]);
  
  // Get company branding information
  const companyName = currentEmpresa?.nombre || 'Mi Empresa';
  const companyLogo = currentEmpresa?.logo || currentEmpresa?.configuracion?.branding?.logo || null;
  const companyEmail = currentEmpresa?.email || '';
  const companyPhone = currentEmpresa?.telefono || '';
  const companyAddress = currentEmpresa?.direccion || '';

  const numeroMostrar = numeroFacturaParaMostrar(factura.numero, factura.canalVenta);

  const imprimirFactura = () => {
    window.print();
  };

  /** mailto: no puede adjuntar archivos; primero generamos y descargamos el PDF real, luego abrimos el correo. */
  const enviarPorEmail = async () => {
    if (!factura.cliente?.email) {
      alert('No hay email del cliente para enviar la factura.');
      return;
    }

    setPdfBusy(true);
    try {
      if (empresaId) await fetchPaymentMethods(empresaId);
      const st = usePaymentMethodsStore.getState();
      const methodsForPdf = st.paymentMethods
        .filter((pm) => pm.empresa_id === empresaId && pm.is_active && pm.show_on_invoices)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      const blob = await buildFacturaPdfBlob(factura, {
        companyName,
        companyLogo,
        companyEmail,
        companyPhone,
        companyAddress,
        paymentMethods: methodsForPdf,
      });
      const fname = safeFacturaPdfFilename(numeroMostrar);
      downloadFacturaPdfBlob(blob, fname);
      openFacturaPdfBlobInNewTab(blob);

      const subject = `Factura ${numeroMostrar} - Clientelo`;
      const body = `Estimado/a ${factura.cliente.nombre},

Adjunto encontrará la factura ${numeroMostrar} por un total de $${factura.total.toLocaleString('es-CO')}.

Detalles de la factura:
- Número: ${numeroMostrar}
- Fecha: ${factura.fechaCreacion.toLocaleDateString('es-ES')}
- Fecha de entrega: ${factura.fechaEntrega.toLocaleDateString('es-ES')}
- Total: $${factura.total.toLocaleString('es-CO')}

${factura.notas ? `Notas: ${factura.notas}` : ''}

Para cualquier consulta, no dude en contactarnos.

Saludos cordiales,
Equipo Clientelo`;

      alert(
        `Se generó el PDF (${fname}).\n\n` +
          `• Revisa tu carpeta de descargas y la pestaña del PDF que se abrió.\n` +
          `• En el correo, usa «Adjuntar archivo» y elige ese PDF.\n\n` +
          `(Ningún navegador puede adjuntar el PDF automáticamente al abrir el correo.)`,
      );

      const mailtoLink = `mailto:${factura.cliente.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink);
    } catch (e) {
      console.error('enviarPorEmail', e);
      alert('No se pudo generar el PDF. Usa «Descargar PDF» o imprime desde el navegador.');
    } finally {
      setPdfBusy(false);
    }
  };

  const descargarPDF = async () => {
    setPdfBusy(true);
    try {
      if (empresaId) await fetchPaymentMethods(empresaId);
      const st = usePaymentMethodsStore.getState();
      const methodsForPdf = st.paymentMethods
        .filter((pm) => pm.empresa_id === empresaId && pm.is_active && pm.show_on_invoices)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      const blob = await buildFacturaPdfBlob(factura, {
        companyName,
        companyLogo,
        companyEmail,
        companyPhone,
        companyAddress,
        paymentMethods: methodsForPdf,
      });
      downloadFacturaPdfBlob(blob, safeFacturaPdfFilename(numeroMostrar));
    } catch (e) {
      console.error('descargarPDF', e);
      alert('No se pudo generar el PDF. Intenta de nuevo o usa Imprimir → Guardar como PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  const compartirPDF = async () => {
    if (!shareAvailable) {
      alert(
        'Compartir archivo PDF no está disponible en este navegador.\n\n' +
          'Prueba Safari en Mac o iPhone, o usa «Descargar PDF».',
      );
      return;
    }
    setPdfBusy(true);
    try {
      if (empresaId) await fetchPaymentMethods(empresaId);
      const st = usePaymentMethodsStore.getState();
      const methodsForPdf = st.paymentMethods
        .filter((pm) => pm.empresa_id === empresaId && pm.is_active && pm.show_on_invoices)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      const blob = await buildFacturaPdfBlob(factura, {
        companyName,
        companyLogo,
        companyEmail,
        companyPhone,
        companyAddress,
        paymentMethods: methodsForPdf,
      });
      const fname = safeFacturaPdfFilename(numeroMostrar);
      const ok = await shareFacturaPdfBlob(blob, fname, {
        title: `Factura ${numeroMostrar}`,
        text: `Factura ${numeroMostrar} — ${companyName}`,
      });
      if (!ok) {
        alert('No se pudo abrir el menú Compartir. Usa «Descargar PDF».');
      }
    } catch (e: unknown) {
      const name = e && typeof e === 'object' && 'name' in e ? String((e as { name: string }).name) : '';
      if (name === 'AbortError') {
        /* usuario cerró la hoja Compartir */
        return;
      }
      console.error('compartirPDF', e);
      alert('No se pudo generar el PDF. Intenta «Descargar PDF».');
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10060] p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Vista de Factura: {numeroMostrar}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={imprimirFactura}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                title="Imprimir"
              >
                <PrinterIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => void descargarPDF()}
                disabled={pdfBusy}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 disabled:opacity-40"
                title={pdfBusy ? 'Generando PDF…' : 'Descargar PDF'}
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => void compartirPDF()}
                disabled={pdfBusy}
                className={`p-2 rounded-full hover:bg-gray-100 disabled:opacity-40 ${
                  shareAvailable ? 'text-blue-600 hover:text-blue-700' : 'text-gray-300 cursor-not-allowed'
                }`}
                title={
                  pdfBusy
                    ? 'Generando PDF…'
                    : shareAvailable
                      ? 'Compartir PDF (AirDrop, Mail, Mensajes…)'
                      : 'Compartir no disponible en este navegador'
                }
              >
                <Share2 className="w-5 h-5" aria-hidden />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Contenido de la factura - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6 print:p-0">
            {/* Company Branding */}
         <div className="flex items-center mb-6 print:mb-4">
           {companyLogo ? (
             <img 
               src={companyLogo} 
               alt={companyName}
               className="h-16 w-auto max-w-48 mr-4 object-contain"
             />
           ) : (
             <div className="h-16 w-16 mr-4 bg-blue-600 text-white rounded-lg flex items-center justify-center text-2xl font-bold">
               {companyName.charAt(0).toUpperCase()}
             </div>
           )}
           <div>
             <h1 className="text-2xl font-bold text-gray-900">{companyName}</h1>
             <div className="text-sm text-gray-600 mt-1">
               {companyEmail && <p><strong>Email:</strong> {companyEmail}</p>}
               {companyPhone && <p><strong>Teléfono:</strong> {companyPhone}</p>}
               {companyAddress && <p><strong>Dirección:</strong> {companyAddress}</p>}
             </div>
           </div>
         </div>

            {/* Encabezado de la factura */}
            <div className="flex justify-between items-start mb-8 print:mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">FACTURA</h2>
                <div className="text-sm text-gray-600">
                  <p><strong>Número:</strong> {numeroMostrar}</p>
                  <p><strong>Fecha:</strong> {factura.fechaCreacion.toLocaleDateString('es-ES')}</p>
                  <p><strong>Entrega:</strong> {factura.fechaEntrega.toLocaleDateString('es-ES')}</p>
                  {factura.metodoPago ? (
                    <p>
                      <strong>Forma de pago (esta factura):</strong>{' '}
                      {metodoPagoFacturaLabel(factura.metodoPago)}
                    </p>
                  ) : null}
                  {factura.estadoPago ? (
                    <p>
                      <strong>Estado de pago:</strong> {estadoPagoFacturaLabel(factura.estadoPago)}
                    </p>
                  ) : null}
                  {factura.feEstado ? (
                    <div className="mt-2">
                      <FeEstadoBadge
                        estado={factura.feEstado}
                        cufe={factura.feCufe}
                        error={factura.feError}
                        showCufe
                      />
                    </div>
                  ) : null}
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  <p><strong>Estado:</strong> {factura.estado}</p>
                  <p><strong>Cliente:</strong> {factura.cliente?.nombre || 'Sin cliente'}</p>
                  <p><strong>Email:</strong> {factura.cliente?.email || 'Sin email'}</p>
                  <p><strong>Teléfono:</strong> {factura.cliente?.telefono || 'Sin teléfono'}</p>
                  {factura.cliente?.numeroDocumento && (
                    <p><strong>Documento:</strong> {factura.cliente.tipoDocumento}: {factura.cliente.numeroDocumento}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Tabla de items */}
            <div className="mb-8 print:mb-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-900">
                      Producto/Servicio
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-900">
                      Descripción
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-center text-sm font-medium text-gray-900">
                      Cantidad
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-900">
                      Precio Unitario
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-900">
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {factura.items.map((item, index) => (
                    <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                        {labelFacturaItemProducto(item)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">
                        {item.descripcion}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-center">
                        {labelFacturaItemCantidad(item)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-right">
                        ${item.precioUnitario.toLocaleString('es-CO')}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-right">
                        ${item.subtotal.toLocaleString('es-CO')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totales */}
            <div className="flex justify-end mb-8 print:mb-6">
              <div className="w-80">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal sin IVA:</span>
                    <span>${factura.subtotalSinIva.toLocaleString('es-CO')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>IVA:</span>
                    <span>${factura.totalIva.toLocaleString('es-CO')}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-medium">Subtotal con IVA:</span>
                    <span className="font-medium">${factura.subtotal.toLocaleString('es-CO')}</span>
                  </div>
                  {factura.descuento > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Descuento:</span>
                      <span>-${factura.descuento.toLocaleString('es-CO')}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 font-medium text-lg">
                    <div className="flex justify-between">
                      <span>Total:</span>
                      <span>${factura.total.toLocaleString('es-CO')}</span>
                    </div>
                  </div>
                  {(factura.montoPagado ?? 0) > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Pagado:</span>
                      <span>{formatearMoneda(factura.montoPagado ?? 0)}</span>
                    </div>
                  )}
                  {(factura.montoPendiente ?? Math.max(0, factura.total - (factura.montoPagado ?? 0))) > 0 && (
                    <div className="flex justify-between text-amber-800 font-medium">
                      <span>Saldo pendiente:</span>
                      <span>
                        {formatearMoneda(
                          factura.montoPendiente ??
                            Math.max(0, factura.total - (factura.montoPagado ?? 0))
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-8 print:mb-6">
              <HistorialPagosPanel factura={factura} companyName={companyName} defaultExpanded />
            </div>

            {/* Notas y condiciones */}
            {(factura.notas || factura.condiciones) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 print:mb-6">
                {factura.notas && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Notas</h4>
                    <p className="text-sm text-gray-600">{factura.notas}</p>
                  </div>
                )}
                
                {factura.condiciones && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Condiciones de Pago</h4>
                    <p className="text-sm text-gray-600">{factura.condiciones}</p>
                  </div>
                )}
              </div>
            )}

            {/* Métodos de Pago */}
            {paymentMethods.length > 0 && (
              <div className="mb-8 print:mb-6">
                <h4 className="text-sm font-bold text-gray-900 mb-4">💳 Métodos de Pago Disponibles:</h4>
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                      <div key={method.id} className="p-3 bg-gray-50 border-l-4 border-blue-600 rounded">
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          <span>{method.icon || '💳'}</span>
                          <span>{method.method_name}</span>
                        </div>
                        {method.description ? (
                          <p className="mt-1 text-sm text-gray-600">{method.description}</p>
                        ) : null}
                        <PaymentMethodMedia
                          accountInfo={(method.account_info || {}) as Record<string, unknown>}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Información adicional */}
            <div className="text-xs text-gray-500 text-center border-t pt-4 print:pt-2">
              <p>Esta factura fue generada por {companyName}</p>
              <p>Para consultas, contacta a tu representante de ventas</p>
              {companyEmail && <p>Email: {companyEmail}</p>}
              {companyPhone && <p>Teléfono: {companyPhone}</p>}
            </div>
          </div>
        </div>

        {/* Footer con acciones - Fixed at bottom */}
        <div className="px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Estado:</span> {factura.estado}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => void enviarPorEmail()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                disabled={!factura.cliente?.email || pdfBusy}
                title={!factura.cliente?.email ? 'No hay email del cliente' : 'Generar PDF y abrir correo'}
              >
                <EnvelopeIcon className="w-4 h-4" />
                <span>Enviar por Email</span>
              </button>
              
              <button
                onClick={onEnviar}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                <span>Enviar por Mensaje</span>
              </button>
              
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
